import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { AgencyProvider } from "@/context/AgencyContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Layout } from "@/components/Layout";

import Login from "@/pages/Login";
import AreaClientiLogin from "@/pages/AreaClientiLogin";
import Dashboard from "@/pages/Dashboard";
import Clients from "@/pages/Clients";
import Policies from "@/pages/Policies";
import PolicyDeadlines from "@/pages/PolicyDeadlines";
import Quotes from "@/pages/Quotes";
import QuoteOffers from "@/pages/QuoteOffers";
import Signatures from "@/pages/Signatures";
import SignatureCenter from "@/pages/SignatureCenter";
import OfferPublicView from "@/pages/OfferPublicView";
import PartnerOfferSubmit from "@/pages/PartnerOfferSubmit";
import OfferSent from "@/pages/OfferSent";
import SignDocument from "@/pages/SignDocument";
import Users from "@/pages/Users";
import Portal from "@/pages/Portal";
import Companies from "@/pages/Companies";
import Brokers from "@/pages/Brokers";
import Commissions from "@/pages/Commissions";
import CommissionsStatement from "@/pages/CommissionsStatement";
import CommissionsCollected from "@/pages/CommissionsCollected";
import ClientArea from "@/pages/ClientArea";
import BrokerArea from "@/pages/BrokerArea";
import AgencySettings from "@/pages/AgencySettings";
import LookupLists from "@/pages/LookupLists";
import MessageTemplates from "@/pages/MessageTemplates";
import PdfTemplates from "@/pages/PdfTemplates";
import PolicyCategories from "@/pages/PolicyCategories";
import PolicyTypes from "@/pages/PolicyTypes";
import PolicyCoverages from "@/pages/PolicyCoverages";
import OfferLineItems from "@/pages/OfferLineItems";
import BusinessInsights from "@/pages/BusinessInsights";
import PhoneDirectory from "@/pages/PhoneDirectory";
import ResetPassword from "@/pages/ResetPassword";
import CronRuns from "@/pages/CronRuns";
import PublicQuoteRequest from "@/pages/PublicQuoteRequest";
import Messages from "@/pages/Messages";
import CompanyArea from "@/pages/CompanyArea";
import PolicyPayment from "@/pages/PolicyPayment";
import PaymentResult from "@/pages/PaymentResult";
import PayPalCheckout from "@/pages/PayPalCheckout";
import PaymentReceipt from "@/pages/PaymentReceipt";
import AgencyPaymentSettings from "@/pages/AgencyPaymentSettings";
import StripeCanaryTest from "@/pages/StripeCanaryTest";
import { OtpGate } from "@/components/OtpGate";
import AdminPayments from "@/pages/AdminPayments";
import PolicyWizard from "@/pages/PolicyWizard";
import PolicyQualityReport from "@/pages/PolicyQualityReport";
import BrokerCommissionsReport from "@/pages/BrokerCommissionsReport";
import TaxRatesSettings from "@/pages/TaxRatesSettings";
import ProductsCatalog from "@/pages/ProductsCatalog";
import Receipts from "@/pages/Receipts";
import CashRegister from "@/pages/CashRegister";
import AgencyReplacementReasons from "@/pages/AgencyReplacementReasons";
import PublicQuoteAccept from "@/pages/PublicQuoteAccept";
import Automations from "@/pages/Automations";
import RequiredDocuments from "@/pages/RequiredDocuments";
import QuoteNotificationPrefs from "@/pages/QuoteNotificationPrefs";
import MyNotificationPrefs from "@/pages/MyNotificationPrefs";
import MySecurityPrefs from "@/pages/MySecurityPrefs";
import AgencySignup from "@/pages/AgencySignup";
import SuperAdminDashboard from "@/pages/SuperAdminDashboard";
import SuperAdminStorage from "@/pages/SuperAdminStorage";
import SuperAdminContacts from "@/pages/SuperAdminContacts";
import SuperAdminAndroidApp from "@/pages/SuperAdminAndroidApp";
import SuperAdminLayout from "@/components/SuperAdminLayout";
import SuperAdminGdpr from "@/pages/SuperAdminGdpr";
import { PrivacyGate } from "@/components/PrivacyGate";
import { CookieBanner } from "@/components/CookieBanner";
import Landing from "@/pages/Landing";
import LandingManager from "@/pages/LandingManager";
import AgencyOnboarding from "@/pages/AgencyOnboarding";
import SaasPayments from "@/pages/SaasPayments";
import Inbox from "@/pages/Inbox";
import Outbox from "@/pages/Outbox";
import Notes from "@/pages/Notes";
import Agenda from "@/pages/Agenda";
import AgencyClosures from "@/pages/AgencyClosures";
import EmailSettings from "@/pages/EmailSettings";
import PublicPageSettings from "@/pages/PublicPageSettings";
import RevisioneUpdate from "@/pages/RevisioneUpdate";
import BolloUpdate from "@/pages/BolloUpdate";
import AgencyAttachments from "@/pages/AgencyAttachments";
import PublicLeads from "@/pages/PublicLeads";
import CfCleanup from "@/pages/CfCleanup";
import { MultiFileUploadDialog } from "@/components/MultiFileUploadDialog";

const ROLES_CLIENT = ["cliente"];
const ROLES_BROKER = ["broker"];
const ROLES_COMPANY = ["company"];
const ROLES_STAFF = ["admin", "agente", "backoffice", "collaboratore"];
const ROLES_ADMIN_BACKOFFICE = ["admin", "backoffice"];
const ROLES_ADMIN = ["admin"];

function App() {
  return (
    <AuthProvider>
      <AgencyProvider>
        <BrowserRouter>
        <Toaster position="top-center" richColors />
        <PrivacyGate />
        <CookieBanner />
        <MultiFileUploadDialog />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/gestione-ufficio" element={<Login />} />
          <Route path="/login-staff" element={<Login />} />
          <Route path="/area-clienti" element={<AreaClientiLogin />} />
          <Route path="/portale-clienti" element={<AreaClientiLogin />} />
          <Route path="/signup" element={<AgencySignup />} />
          <Route path="/landing" element={<Landing />} />
          <Route path="/agenzia-onboarding/:token" element={<AgencyOnboarding />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/richiedi-preventivo" element={<PublicQuoteRequest />} />
          <Route path="/richiedi-preventivo/:agencySlug" element={<PublicQuoteRequest />} />
          <Route path="/offerta/:token" element={<OfferPublicView />} />
          <Route path="/inserisci-offerta/:token" element={<PartnerOfferSubmit />} />
          <Route path="/offerta-inviata" element={<OfferSent />} />
          <Route path="/sign/:sigId" element={<SignDocument />} />
          <Route path="/preventivo/:token" element={<PublicQuoteAccept />} />
          <Route path="/revisione/:token" element={<RevisioneUpdate />} />
          <Route path="/bollo/:token" element={<BolloUpdate />} />
          <Route
            path="/area-riservata"
            element={
              <ProtectedRoute roles={ROLES_CLIENT}>
                <ClientArea />
              </ProtectedRoute>
            }
          />
          <Route
            path="/area-riservata/pagamento/:policyId"
            element={
              <ProtectedRoute roles={ROLES_CLIENT}>
                <PolicyPayment />
              </ProtectedRoute>
            }
          />
          <Route
            path="/area-riservata/pagamento/paypal/:txId"
            element={
              <ProtectedRoute roles={ROLES_CLIENT}>
                <PayPalCheckout />
              </ProtectedRoute>
            }
          />
          <Route
            path="/area-riservata/pagamento/esito"
            element={
              <ProtectedRoute roles={ROLES_CLIENT}>
                <PaymentResult />
              </ProtectedRoute>
            }
          />
          <Route
            path="/area-riservata/ricevuta/:txId"
            element={
              <ProtectedRoute roles={ROLES_CLIENT}>
                <PaymentReceipt />
              </ProtectedRoute>
            }
          />
          <Route
            path="/area-broker"
            element={
              <ProtectedRoute roles={ROLES_BROKER}>
                <BrokerArea />
              </ProtectedRoute>
            }
          />
          <Route
            path="/area-compagnia"
            element={
              <ProtectedRoute roles={ROLES_COMPANY}>
                <CompanyArea />
              </ProtectedRoute>
            }
          />
          <Route
            element={
              <ProtectedRoute roles={["super_admin"]}>
                <SuperAdminLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/superadmin" element={<SuperAdminDashboard />} />
            <Route path="/superadmin/storage" element={<SuperAdminStorage />} />
            <Route path="/superadmin/gdpr" element={<SuperAdminGdpr />} />
            <Route path="/superadmin/android-app" element={<SuperAdminAndroidApp />} />
            <Route path="/superadmin/contacts" element={<SuperAdminContacts />} />
            <Route path="/superadmin/landing" element={<LandingManager />} />
            <Route path="/superadmin/payments" element={<SaasPayments />} />
          </Route>
          <Route
            element={
              <ProtectedRoute roles={ROLES_STAFF}>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/leads" element={<PublicLeads />} />
            <Route
              path="/admin/cf-cleanup"
              element={
                <ProtectedRoute roles={ROLES_ADMIN_BACKOFFICE}>
                  <CfCleanup />
                </ProtectedRoute>
              }
            />
            <Route path="/policies" element={<Policies />} />
            <Route path="/policies/deadlines" element={<PolicyDeadlines />} />
            <Route path="/policies/wizard" element={<PolicyWizard />} />
            <Route path="/policies/quality-report" element={<PolicyQualityReport />} />
            <Route path="/agency/tax-rates" element={<TaxRatesSettings />} />
            <Route path="/agency/products" element={<ProductsCatalog />} />
            <Route path="/agency/receipts" element={<Receipts />} />
            <Route path="/cash-register" element={<CashRegister />} />
            <Route path="/agency/replacement-reasons" element={<AgencyReplacementReasons />} />
            <Route path="/quotes/wizard" element={<PolicyWizard mode="quote" />} />
            <Route path="/quotes" element={<Quotes />} />
            <Route path="/quotes/offers" element={<QuoteOffers />} />
            <Route path="/signatures" element={<Signatures />} />
            <Route path="/signature-center" element={<SignatureCenter />} />
            <Route path="/inbox" element={<Inbox />} />
            <Route path="/outbox" element={<Outbox />} />
            <Route path="/notes" element={<Notes />} />
            <Route path="/agenda" element={<Agenda />} />
            <Route path="/agency/closures" element={<AgencyClosures />} />
            <Route path="/agency/email-settings" element={
              <OtpGate
                purpose="admin_sensitive"
                requestPath="/admin/sensitive-gate/request-otp"
                verifyPath="/admin/sensitive-gate/verify-otp"
                title="Impostazioni Email"
                description="Sezione ad alta sensibilità (credenziali SMTP/IMAP). Richiesta verifica OTP."
              >
                <EmailSettings />
              </OtpGate>
            } />
            <Route path="/agency/public-page" element={<PublicPageSettings />} />
            <Route path="/commissions" element={<Commissions />} />
            <Route path="/commissions/statement" element={<CommissionsStatement />} />
            <Route path="/commissions/collected" element={<CommissionsCollected />} />
            <Route path="/commissions/broker-report" element={<BrokerCommissionsReport />} />
            <Route path="/messaggi" element={<Messages />} />
            <Route
              path="/admin/pagamenti"
              element={
                <ProtectedRoute roles={ROLES_ADMIN_BACKOFFICE}>
                  <AdminPayments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pagamenti/ricevuta/:txId"
              element={
                <ProtectedRoute roles={ROLES_STAFF}>
                  <PaymentReceipt />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pagamenti/config"
              element={
                <ProtectedRoute roles={ROLES_ADMIN}>
                  <OtpGate
                    purpose="admin_sensitive"
                    requestPath="/admin/sensitive-gate/request-otp"
                    verifyPath="/admin/sensitive-gate/verify-otp"
                    title="Configurazione Pagamenti"
                    description="Sezione ad alta sensibilità (chiavi Stripe/PayPal, IBAN, dati bancari). Richiesta verifica OTP."
                  >
                    <AgencyPaymentSettings />
                  </OtpGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/stripe-test"
              element={
                <ProtectedRoute roles={ROLES_ADMIN}>
                  <StripeCanaryTest />
                </ProtectedRoute>
              }
            />
            <Route path="/companies" element={<ProtectedRoute roles={ROLES_ADMIN_BACKOFFICE}><Companies /></ProtectedRoute>} />
            <Route path="/brokers" element={<ProtectedRoute roles={ROLES_ADMIN_BACKOFFICE}><Brokers /></ProtectedRoute>} />
            <Route
              path="/users"
              element={
                <ProtectedRoute roles={ROLES_ADMIN}>
                  <OtpGate
                    purpose="admin_sensitive"
                    requestPath="/admin/sensitive-gate/request-otp"
                    verifyPath="/admin/sensitive-gate/verify-otp"
                    title="Gestione Utenti"
                    description="Sezione ad alta sensibilità (permessi, credenziali, ruoli). Richiesta verifica OTP."
                  >
                    <Users />
                  </OtpGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/agency"
              element={
                <ProtectedRoute roles={ROLES_ADMIN}>
                  <OtpGate
                    purpose="admin_sensitive"
                    requestPath="/admin/sensitive-gate/request-otp"
                    verifyPath="/admin/sensitive-gate/verify-otp"
                    title="Anagrafica Agenzia"
                    description="Sezione ad alta sensibilità (dati fiscali, indirizzi, contatti agenzia). Richiesta verifica OTP."
                  >
                    <AgencySettings />
                  </OtpGate>
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/lookup-lists"
              element={
                <ProtectedRoute roles={ROLES_ADMIN}>
                  <LookupLists />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/message-templates"
              element={
                <ProtectedRoute roles={ROLES_ADMIN}>
                  <MessageTemplates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/pdf-templates"
              element={
                <ProtectedRoute roles={ROLES_ADMIN_BACKOFFICE}>
                  <PdfTemplates />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/categories"
              element={
                <ProtectedRoute roles={ROLES_ADMIN_BACKOFFICE}>
                  <PolicyCategories />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/policy-types"
              element={
                <ProtectedRoute roles={ROLES_ADMIN_BACKOFFICE}>
                  <PolicyTypes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/policy-coverages"
              element={
                <ProtectedRoute roles={ROLES_ADMIN_BACKOFFICE}>
                  <PolicyCoverages />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/offer-line-items"
              element={
                <ProtectedRoute roles={ROLES_ADMIN_BACKOFFICE}>
                  <OfferLineItems />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/insights"
              element={
                <ProtectedRoute roles={["admin", "backoffice", "agente"]}>
                  <BusinessInsights />
                </ProtectedRoute>
              }
            />
            <Route
              path="/rubrica"
              element={
                <ProtectedRoute roles={["admin", "agente", "backoffice"]}>
                  <PhoneDirectory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agency/attachments"
              element={
                <ProtectedRoute roles={["admin", "agente", "backoffice"]}>
                  <AgencyAttachments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/cron"
              element={
                <ProtectedRoute roles={ROLES_ADMIN}>
                  <CronRuns />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/automations"
              element={
                <ProtectedRoute roles={ROLES_ADMIN_BACKOFFICE}>
                  <Automations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/required-documents"
              element={
                <ProtectedRoute roles={ROLES_ADMIN_BACKOFFICE}>
                  <RequiredDocuments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/quote-notifications"
              element={
                <ProtectedRoute roles={["admin"]}>
                  <QuoteNotificationPrefs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/notifications"
              element={
                <ProtectedRoute roles={["admin", "agente", "backoffice"]}>
                  <MyNotificationPrefs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/security"
              element={
                <ProtectedRoute roles={["admin", "agente", "backoffice", "super_admin"]}>
                  <MySecurityPrefs />
                </ProtectedRoute>
              }
            />
            <Route path="/portal" element={<Portal />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      </AgencyProvider>
    </AuthProvider>
  );
}

export default App;
